import { useSetState } from '@/shared/lib/customHooks';
import { checkNullId, checkToFormatPhoneNumber } from '@/shared/lib';
import { CONTACT_KEY, DEFAULT_CONTACT } from '@/entities/rma';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { checkShipmentData } from '../../lib/editShipmentModal';
import type { EditShipmentModalProps } from './editShipmentModal.types';

const idArr = [
  CONTACT_KEY.name,
  CONTACT_KEY.company,
  CONTACT_KEY.country,
  CONTACT_KEY.street1,
  CONTACT_KEY.street2,
  CONTACT_KEY.city,
  CONTACT_KEY.state,
  CONTACT_KEY.zip,
  CONTACT_KEY.phone,
  CONTACT_KEY.email,
];

/**
 * Runs the edit-shipment contact form of the shipping label flow.
 * Resets the contact fields and errors from data whenever it changes, handles field, country and
 * address autocomplete edits and Enter-to-next-field focus, derives the selected country and state
 * options, and validates the contact before passing it to updateData.
 *
 * @param props - The shipment contact data, the updateData callback and the US, Canadian state and
 *   country option lists.
 * @returns The form state, field ids, change and save handlers, the phone-format flag and the
 *   selected country and state options.
 */
export const useEditShipmentModal = ({
  data,
  updateData,
  listStatesUS,
  listStatesCA,
  listCountries,
}: Pick<
  EditShipmentModalProps,
  'data' | 'updateData' | 'listStatesUS' | 'listStatesCA' | 'listCountries'
>) => {
  const [state, setState] = useSetState<any>({
    isSaving: false,
    ...DEFAULT_CONTACT,
    errorObject: {
      ...DEFAULT_CONTACT,
    },
  });

  /**
   * Moves focus to the next shipment field when Enter is pressed. Looks the event target's id up in the ordered field list and focuses the following element when both ids are valid, deliberately not preventing the default so react-select can still pick an option.
   *
   * @param e - Keyboard event whose target id locates the current field.
   * @returns Nothing.
   */
  const handleKeyDown = (e: any) => {
    const idIndex = _.indexOf(idArr, e.target.id) !== -1 ? _.indexOf(idArr, e.target.id) : null;
    if (
      idIndex !== null &&
      e.key === 'Enter' &&
      checkNullId(idArr[idIndex]) &&
      checkNullId(idArr[idIndex + 1])
    ) {
      // *: Do not use preventDefault to allow react select input select option when pressing Enter
      // e.preventDefault();
      document.getElementById(idArr[idIndex + 1])?.focus();
    }
  };

  /**
   * Applies a new shipment country and discards the address details tied to the previous one. Sets the named country field and resets street1, street2, state, zip and city to their contact defaults, mirroring the reset into the error object.
   *
   * @param name - Field name of the country being set.
   * @param value - New country value.
   * @returns Nothing.
   */
  const onChangeCountry = (name: any, value: any) => {
    const resetValue = _.pick(DEFAULT_CONTACT, ['street1', 'street2', 'state', 'zip', 'city']);
    setState({
      [name]: value,
      ...resetValue,
      errorObject: {
        ...state.errorObject,
        ...resetValue,
      },
    });
  };

  /**
   * Fills the shipment address fields from a selected address suggestion. Copies whichever of address, city, state code and zip the suggestion carries into street1, city, state and zip, and clears the error entry for each field it sets.
   *
   * @param data - Selected address suggestion with optional address, city, state and zip.
   * @returns Nothing.
   */
  const onChangeAddress = (data: any) => {
    const objectState = {};
    const errorState = {};
    if (data?.address) {
      _.assign(objectState, { street1: data?.address });
      _.assign(errorState, { street1: '' });
    }
    if (data?.city) {
      _.assign(objectState, { city: data?.city });
      _.assign(errorState, { city: '' });
    }

    if (data?.state?.code) {
      _.assign(objectState, { state: data?.state?.code });
      _.assign(errorState, { state: '' });
    }
    if (data?.zip) {
      _.assign(objectState, { zip: data?.zip });
      _.assign(errorState, { zip: '' });
    }

    setState({
      ...objectState,
      errorObject: {
        ...state.errorObject,
        ...errorState,
      },
    });
  };

  /**
   * Fills the shipment location fields from a selected city suggestion. Copies whichever of city, state code and zip the suggestion carries into the matching fields and clears the error entry for each one it sets.
   *
   * @param data - Selected city suggestion with optional city, state and zip.
   * @returns Nothing.
   */
  const onChangeCity = (data: any) => {
    const objectState = {};
    const errorState = {};
    if (data?.city) {
      _.assign(objectState, { city: data?.city });
      _.assign(errorState, { city: '' });
    }

    if (data?.state?.code) {
      _.assign(objectState, { state: data?.state?.code });
      _.assign(errorState, { state: '' });
    }
    if (data?.zip) {
      _.assign(objectState, { zip: data?.zip });
      _.assign(errorState, { zip: '' });
    }

    setState({
      ...objectState,
      errorObject: {
        ...state.errorObject,
        ...errorState,
      },
    });
  };

  /**
   * Resets the address fields that autocomplete had filled in when the lookup source changes. Restores city, state and zip, plus both street lines unless the change came from the city search, to their contact defaults and clears the matching entries in the error object.
   *
   * @param isSearchCity - Whether the reset was triggered by the city search, which keeps the street lines.
   * @returns Nothing.
   */
  const clearAutoComplete = (isSearchCity: any) => {
    const listKeys = ['city', 'state', 'zip'];
    if (!isSearchCity) {
      listKeys.push('street1', 'street2');
    }
    const resetValue = _.pick(DEFAULT_CONTACT, listKeys);
    setState({
      ...resetValue,
      errorObject: {
        ...state.errorObject,
        ...resetValue,
      },
    });
  };

  /**
   * Writes a single shipment field value into state and clears its validation error. Sets the named key and blanks the same key inside the error object.
   *
   * @param name - Field name being written.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    setState({
      [name]: value,
      errorObject: {
        ...state.errorObject,
        [name]: '',
      },
    });
  };

  /**
   * Validates and submits the edited shipment contact. Picks the contact keys out of state and marks saving, runs the shipment validation and merges any returned errors back into state while cancelling the save, otherwise hands the contact data to the update callback.
   *
   * @returns A promise resolving once the contact has been validated and submitted or the errors merged.
   */
  const handleClickSave = async () => {
    const contactData = _.pick(state, _.values(CONTACT_KEY));
    setState({ isSaving: true });
    const errorObject = await checkShipmentData({
      contacts: contactData,
      listStatesCA,
      listStatesUS,
    });
    if (!_.isEmpty(errorObject)) {
      setState({
        isSaving: false,
        errorObject: {
          ...state.errorObject,
          ...errorObject,
        },
      });
      return;
    }

    updateData(contactData);
  };

  /**
   * Whether the selected shipping country uses US/Canadian phone and state formatting. Memoized on state.country and computed by checkToFormatPhoneNumber, which tests the country code against the US and CA list.
   *
   * @returns True when the country is US or CA.
   */
  const shouldFormatPhoneNumber = useMemo(
    () => checkToFormatPhoneNumber(state.country),
    [state.country],
  );

  /**
   * The currently selected country as a value/label option for the country select. Memoized on state.country: looks the code up in listCountries and yields undefined when no country is set or no entry matches.
   *
   * @returns The option object for the selected country, or undefined.
   */
  const countriesObject = useMemo(() => {
    if (state.country) {
      const findedCountry = _.find(listCountries, { value: state.country });
      if (findedCountry) {
        return {
          value: state.country,
          label: findedCountry.label,
        };
      }

      return undefined;
    }
    return undefined;
  }, [state.country]);

  /**
   * The currently selected state or province as a value/label option for the state select. Memoized on state and country: yields undefined unless a state is set and the country uses formatted phone numbers, then matches the code against the US or Canadian list and yields undefined when nothing matches.
   *
   * @returns The option object for the selected state, or undefined.
   */
  const stateObject = useMemo(() => {
    if (!state.state) {
      return undefined;
    }

    if (shouldFormatPhoneNumber) {
      const listStates = state.country === 'US' ? listStatesUS : listStatesCA;
      const findedState = _.find(listStates, { value: state.state });
      if (findedState) {
        return {
          value: state.state,
          label: findedState?.label,
        };
      }
      return undefined;
    }

    return undefined;
  }, [state.state, state.country, listStatesUS, listStatesCA]);

  useEffect(() => {
    setState({
      ...DEFAULT_CONTACT,
      ...data,
      errorObject: {
        ...DEFAULT_CONTACT,
      },
      isSaving: false,
    });
  }, [data]);

  return {
    state,
    idArr,
    handleKeyDown,
    onChangeCountry,
    onChangeAddress,
    onChangeCity,
    clearAutoComplete,
    onChange,
    handleClickSave,
    shouldFormatPhoneNumber,
    countriesObject,
    stateObject,
  };
};
